import BoldText from '@/components/BoldText';
import LayoutSettings from '@/components/LayoutSetting/LayoutSettings';
import { PlanMessage } from '@/components/PlanMessage';
import { AvailabilityType, PricingPlan, ProductOverrideAction, ProductOverrideType } from '@/constants/enum';
import {
  deliveryCollectionSelector,
  deliveryDisableSelector,
  deliveryOverrideDateRangesSelector,
  deliveryOverrideHolidaysSelector,
  deliveryOverrideSpecificWorkingDaysSelector,
  deliveryProductSelector,
  deliverySettingSelector,
  handleSaveDeliverySettings,
  handleSaveOverrideDeliveryDateRanges,
  handleSaveOverrideDeliveryHolidays,
  handleSaveOverrideDeliverySpecificWorkingDays,
  handleSetDeliveryState,
} from '@/redux/slice/delivery.slice';
import { handleSetErrorFields } from '@/redux/slice/error.slice';
import { DateRange } from '@/types';
import {
  IDeliveryOverrideDateRanges,
  IDeliveryOverrideHolidays,
  IDeliveryOverrideSpecificWorkingDays,
  IDeliverySettings,
} from '@/types/components/delivery';
import { BlockStack, Button, Divider, InlineGrid, InlineStack, Select } from '@shopify/polaris';
import { PlusIcon } from '@shopify/polaris-icons';
import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DateRangeBlock from './DateRangeBlock';
import OverrideTimeBlock from './OverrideTimeBlock';

interface OverrideActionParams {
  action: ProductOverrideAction;
  index?: number;
  dateRangeIndex?: number;
  updatedDateRange?: DateRange;
  filteredItems?: string[];
  dayValue?: number;
}

interface Resource {
  /** in GraphQL id format, ie 'gid://shopify/Product/1' */
  id: string;
}

interface BaseResource extends Resource {
  variants?: Resource[];
}

export const ProductOverride = () => {
  const { t } = useTranslation('deliverySettings');
  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryOverrideSpecificWorkingDays = useSelector(deliveryOverrideSpecificWorkingDaysSelector);
  const deliveryOverrideHolidays = useSelector(deliveryOverrideHolidaysSelector);
  const deliveryOverrideDateRanges = useSelector(deliveryOverrideDateRangesSelector);
  const deliveryProducts = useSelector(deliveryProductSelector);
  const deliveryCollections = useSelector(deliveryCollectionSelector);
  const overrideSpecificWorkingDaysProductsIdChosen = deliveryOverrideSpecificWorkingDays.flatMap((item) => item.productIds);
  const overrideSpecificWorkingDaysCollectionsIdChosen = deliveryOverrideSpecificWorkingDays.flatMap(
    (item) => item.collectionIds,
  );
  const overrideHolidaysProductsIdChosen = deliveryOverrideHolidays.flatMap((item) => item.productIds);
  const overrideHolidaysCollectionsIdChosen = deliveryOverrideHolidays.flatMap((item) => item.collectionIds);
  const overrideDateRangeProductsIdChosen = deliveryOverrideDateRanges.flatMap((item) => item.productIds);
  const overrideDateRangeCollectionsIdChosen = deliveryOverrideDateRanges.flatMap((item) => item.collectionIds);
  const deliveryDisable = useSelector(deliveryDisableSelector);
  const deliverySettingsRef = useRef(deliverySettings);
  const dispatch = useDispatch();

  useEffect(() => {
    deliverySettingsRef.current = deliverySettings;
  }, [deliverySettings]);

  const overrideTypeOptions = [
    {
      label: t('productOverride.overrideTypeOptions.0'),
      value: ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS.toString(),
    },
    {
      label: t('productOverride.overrideTypeOptions.1'),
      value: ProductOverrideType.OVERRIDE_HOLIDAYS.toString(),
    },
    {
      label: t('productOverride.overrideTypeOptions.2'),
      value: ProductOverrideType.OVERRIDE_DATE_RANGES.toString(),
    },
  ];

  const hasEmptyDates = (overrides: IDeliveryOverrideSpecificWorkingDays[] | IDeliveryOverrideHolidays[]) => {
    return overrides.some(
      (item) => !item.overrideDates.length || item.overrideDates.some((date) => !date.startDate || !date.endDate),
    );
  };

  const hasEmptyProductsAndCollections = (
    overrides: IDeliveryOverrideSpecificWorkingDays[] | IDeliveryOverrideHolidays[] | IDeliveryOverrideDateRanges[],
  ) => overrides.some((item) => !item.collectionIds.length && !item.productIds.length);

  async function handleShopifySelection({
    type,
    action,
    selectionIds,
    idRegex,
    filteredIds,
    getDeliverySettings,
  }: {
    type: 'product' | 'collection';
    action: 'select';
    selectionIds?: BaseResource[];
    idRegex: RegExp;
    filteredIds?: string[];
    getDeliverySettings: () => IDeliverySettings;
  }) {
    const querySpecific = filteredIds?.map((id) => `id:${id}`).join(' OR ');
    const queryAll = filteredIds?.map((id) => `NOT id:${id}`).join(' AND ');
    const deliverySettings = getDeliverySettings();
    const selected = await shopify.resourcePicker({
      type,
      action,
      selectionIds: selectionIds,
      multiple: true,
      filter: {
        hidden: true,
        variants: false,
        draft: false,
        archived: false,
        query: deliverySettings.productAvailabilityType === AvailabilityType.INCLUDED ? querySpecific : queryAll,
      },
    });

    if (selected) {
      const selectedIds = selected
        .map((item) => {
          const match = item.id.match(idRegex);
          return match ? match[1] : null;
        })
        .filter((id): id is string => id !== null);

      return selectedIds;
    }
  }

  const handleOverrideAction = useCallback(
    async ({ action, index, dateRangeIndex, updatedDateRange, filteredItems, dayValue }: OverrideActionParams) => {
      switch (deliverySettings.overrideType) {
        case ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS:
          if (action === ProductOverrideAction.ADD_OVERRIDE_RULE) {
            if (
              hasEmptyDates(deliveryOverrideSpecificWorkingDays) ||
              hasEmptyProductsAndCollections(deliveryOverrideSpecificWorkingDays)
            ) {
              dispatch(handleSetDeliveryState({ isSaving: true }));
              return;
            }
            const newOverrideRule: IDeliveryOverrideSpecificWorkingDays = {
              productIds: [],
              collectionIds: [],
              overrideDates: [{ startDate: '', endDate: '' }],
            };
            const updatedOverrideSpecificWorkingDays = [...deliveryOverrideSpecificWorkingDays, newOverrideRule];
            dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.REMOVE_OVERRIDE_RULE) {
            const updatedOverrideSpecificWorkingDays = deliveryOverrideSpecificWorkingDays.filter((_, i) => i !== index);
            dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
          } else if (action === ProductOverrideAction.ADD_DATE_RANGES) {
            const newDateRange = { startDate: '', endDate: '' };
            const updatedOverrideSpecificWorkingDays = deliveryOverrideSpecificWorkingDays.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDates: [...item.overrideDates, newDateRange],
                  }
                : item,
            );
            dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.REMOVE_DATE_RANGES) {
            const updatedOverrideSpecificWorkingDays = deliveryOverrideSpecificWorkingDays.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDates: item.overrideDates.filter((_, dateIndex) => dateIndex !== dateRangeIndex),
                  }
                : item,
            );

            dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
          } else if (action === ProductOverrideAction.UPDATE_DATE_RANGES) {
            const updatedOverrideSpecificWorkingDays = deliveryOverrideSpecificWorkingDays.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDates: item.overrideDates.map((dateRange, rangeIndex) =>
                      rangeIndex === dateRangeIndex
                        ? {
                            ...dateRange,
                            startDate: updatedDateRange?.since.toString() || '',
                            endDate: updatedDateRange?.until.toString() || '',
                          }
                        : dateRange,
                    ),
                  }
                : item,
            );
            dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.CHOOSE_PRODUCTS && typeof index === 'number') {
            const selectedIds = await handleShopifySelection({
              type: 'product',
              action: 'select',
              selectionIds: deliveryOverrideSpecificWorkingDays[index].productIds.map((item) => ({
                id: `gid://shopify/Product/${item}`,
              })),
              idRegex: /gid:\/\/shopify\/Product\/(\d+)/,
              filteredIds: filteredItems,
              getDeliverySettings: () => deliverySettingsRef.current,
            });
            if (selectedIds) {
              const updatedOverrideSpecificWorkingDays = deliveryOverrideSpecificWorkingDays.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      productIds: selectedIds,
                    }
                  : item,
              );
              dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
            }
          } else if (action === ProductOverrideAction.CHOOSE_COLLECTIONS && typeof index === 'number') {
            const selectedIds = await handleShopifySelection({
              type: 'collection',
              action: 'select',
              selectionIds: deliveryOverrideSpecificWorkingDays[index].collectionIds.map((item) => ({
                id: `gid://shopify/Collection/${item}`,
              })),
              idRegex: /gid:\/\/shopify\/Collection\/(\d+)/,
              filteredIds: filteredItems,
              getDeliverySettings: () => deliverySettingsRef.current,
            });
            if (selectedIds) {
              const updatedOverrideSpecificWorkingDays = deliveryOverrideSpecificWorkingDays.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      collectionIds: selectedIds,
                    }
                  : item,
              );
              dispatch(handleSaveOverrideDeliverySpecificWorkingDays(updatedOverrideSpecificWorkingDays));
            }
          }
          break;
        case ProductOverrideType.OVERRIDE_HOLIDAYS:
          if (action === ProductOverrideAction.ADD_OVERRIDE_RULE) {
            if (hasEmptyDates(deliveryOverrideHolidays) || hasEmptyProductsAndCollections(deliveryOverrideHolidays)) {
              dispatch(handleSetDeliveryState({ isSaving: true }));
              return;
            }
            const newOverrideRule: IDeliveryOverrideHolidays = {
              productIds: [],
              collectionIds: [],
              overrideDates: [{ startDate: '', endDate: '' }],
            };
            const updatedOverrideHolidays = [...deliveryOverrideHolidays, newOverrideRule];
            dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.REMOVE_OVERRIDE_RULE) {
            const updatedOverrideHolidays = deliveryOverrideHolidays.filter((_, i) => i !== index);
            dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
          } else if (action === ProductOverrideAction.ADD_DATE_RANGES) {
            const newDateRange = { startDate: '', endDate: '' };
            const updatedOverrideHolidays = deliveryOverrideHolidays.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDates: [...item.overrideDates, newDateRange],
                  }
                : item,
            );
            dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.REMOVE_DATE_RANGES) {
            const updatedOverrideHolidays = deliveryOverrideHolidays.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDates: item.overrideDates.filter((_, dateIndex) => dateIndex !== dateRangeIndex),
                  }
                : item,
            );

            dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
          } else if (action === ProductOverrideAction.UPDATE_DATE_RANGES) {
            const updatedOverrideHolidays = deliveryOverrideHolidays.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDates: item.overrideDates.map((dateRange, rangeIndex) =>
                      rangeIndex === dateRangeIndex
                        ? {
                            ...dateRange,
                            startDate: updatedDateRange?.since.toString() || '',
                            endDate: updatedDateRange?.until.toString() || '',
                          }
                        : dateRange,
                    ),
                  }
                : item,
            );
            dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.CHOOSE_PRODUCTS && typeof index === 'number') {
            const selectedIds = await handleShopifySelection({
              type: 'product',
              action: 'select',
              selectionIds: deliveryOverrideHolidays[index].productIds.map((item) => ({
                id: `gid://shopify/Product/${item}`,
              })),
              idRegex: /gid:\/\/shopify\/Product\/(\d+)/,
              filteredIds: filteredItems,
              getDeliverySettings: () => deliverySettingsRef.current,
            });
            if (selectedIds) {
              const updatedOverrideHolidays = deliveryOverrideHolidays.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      productIds: selectedIds,
                    }
                  : item,
              );
              dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
            }
          } else if (action === ProductOverrideAction.CHOOSE_COLLECTIONS && typeof index === 'number') {
            const selectedIds = await handleShopifySelection({
              type: 'collection',
              action: 'select',
              selectionIds: deliveryOverrideHolidays[index].collectionIds.map((item) => ({
                id: `gid://shopify/Collection/${item}`,
              })),
              idRegex: /gid:\/\/shopify\/Collection\/(\d+)/,
              filteredIds: filteredItems,
              getDeliverySettings: () => deliverySettingsRef.current,
            });
            if (selectedIds) {
              const updatedOverrideHolidays = deliveryOverrideHolidays.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      collectionIds: selectedIds,
                    }
                  : item,
              );
              dispatch(handleSaveOverrideDeliveryHolidays(updatedOverrideHolidays));
            }
          }
          break;
        case ProductOverrideType.OVERRIDE_DATE_RANGES:
          if (action === ProductOverrideAction.ADD_OVERRIDE_RULE) {
            if (hasEmptyProductsAndCollections(deliveryOverrideDateRanges)) {
              dispatch(handleSetDeliveryState({ isSaving: true }));
              return;
            }
            const newOverrideRule: IDeliveryOverrideDateRanges = {
              productIds: [],
              collectionIds: [],
              overridePreparationDay: 0,
              overrideDeliveryDay: 0,
            };
            const updatedOverrideDateRanges = [...deliveryOverrideDateRanges, newOverrideRule];
            dispatch(handleSaveOverrideDeliveryDateRanges(updatedOverrideDateRanges));
            dispatch(handleSetDeliveryState({ isSaving: false }));
          } else if (action === ProductOverrideAction.REMOVE_OVERRIDE_RULE) {
            const updatedOverrideDateRanges = deliveryOverrideDateRanges.filter((_, i) => i !== index);
            dispatch(handleSaveOverrideDeliveryDateRanges(updatedOverrideDateRanges));
          } else if (action === ProductOverrideAction.CHOOSE_PRODUCTS && typeof index === 'number') {
            const selectedIds = await handleShopifySelection({
              type: 'product',
              action: 'select',
              selectionIds: deliveryOverrideDateRanges[index].productIds.map((item) => ({
                id: `gid://shopify/Product/${item}`,
              })),
              idRegex: /gid:\/\/shopify\/Product\/(\d+)/,
              filteredIds: filteredItems,
              getDeliverySettings: () => deliverySettingsRef.current,
            });
            if (selectedIds) {
              const updatedOverrideDateRanges = deliveryOverrideDateRanges.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      productIds: selectedIds,
                    }
                  : item,
              );
              dispatch(handleSaveOverrideDeliveryDateRanges(updatedOverrideDateRanges));
            }
          } else if (action === ProductOverrideAction.CHOOSE_COLLECTIONS && typeof index === 'number') {
            const selectedIds = await handleShopifySelection({
              type: 'collection',
              action: 'select',
              selectionIds: deliveryOverrideDateRanges[index].collectionIds.map((item) => ({
                id: `gid://shopify/Collection/${item}`,
              })),
              idRegex: /gid:\/\/shopify\/Collection\/(\d+)/,
              filteredIds: filteredItems,
              getDeliverySettings: () => deliverySettingsRef.current,
            });
            if (selectedIds) {
              const updatedOverrideDateRanges = deliveryOverrideDateRanges.map((item, i) =>
                i === index
                  ? {
                      ...item,
                      collectionIds: selectedIds,
                    }
                  : item,
              );
              dispatch(handleSaveOverrideDeliveryDateRanges(updatedOverrideDateRanges));
            }
          } else if (action === ProductOverrideAction.CHANGE_OVERRIDE_PREPARATION_DAY) {
            const updatedOverrideDateRanges = deliveryOverrideDateRanges.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overridePreparationDay: Number(dayValue),
                  }
                : item,
            );
            dispatch(handleSaveOverrideDeliveryDateRanges(updatedOverrideDateRanges));
          } else if (action === ProductOverrideAction.CHANGE_OVERRIDE_DATERANGE_DAY) {
            const updatedOverrideDateRanges = deliveryOverrideDateRanges.map((item, i) =>
              i === index
                ? {
                    ...item,
                    overrideDeliveryDay: Number(dayValue),
                  }
                : item,
            );
            dispatch(handleSaveOverrideDeliveryDateRanges(updatedOverrideDateRanges));
          }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deliverySettings.overrideType,
      deliveryOverrideSpecificWorkingDays,
      dispatch,
      deliveryOverrideHolidays,
      deliveryOverrideDateRanges,
    ],
  );

  useEffect(() => {
    if (deliverySettings.overrideType === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            emptyDateRange: hasEmptyDates(deliveryOverrideSpecificWorkingDays),
            emptyProductsAndCollections: hasEmptyProductsAndCollections(deliveryOverrideSpecificWorkingDays),
          },
        }),
      );
    } else if (deliverySettings.overrideType === ProductOverrideType.OVERRIDE_HOLIDAYS) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            emptyDateRange: hasEmptyDates(deliveryOverrideHolidays),
            emptyProductsAndCollections: hasEmptyProductsAndCollections(deliveryOverrideHolidays),
          },
        }),
      );
    } else if (deliverySettings.overrideType === ProductOverrideType.OVERRIDE_DATE_RANGES) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            emptyDateRange: false,
            emptyProductsAndCollections: hasEmptyProductsAndCollections(deliveryOverrideDateRanges),
          },
        }),
      );
    }
  }, [
    dispatch,
    deliveryOverrideDateRanges,
    deliveryOverrideHolidays,
    deliveryOverrideSpecificWorkingDays,
    deliverySettings.overrideType,
  ]);

  useEffect(() => {
    dispatch(handleSetDeliveryState({ isSaving: false }));
  }, [dispatch, deliverySettings.overrideType]);

  return (
    <LayoutSettings title={t('productOverride.title')} helpText={t('productOverride.description')}>
      <div id="delivery-override-rules">
        <BlockStack gap="400">
          <BlockStack gap="200">
            <InlineStack blockAlign="center" align="space-between">
              <BoldText>{t('productOverride.title')}</BoldText>
              {deliveryDisable.isDisableProductOverride && <PlanMessage plan={PricingPlan.Smart} />}
            </InlineStack>

            <InlineGrid columns="1fr auto" gap="200">
              <Select
                label="Override type"
                value={deliverySettings.overrideType.toString()}
                onChange={(value) => dispatch(handleSaveDeliverySettings({ overrideType: Number(value) }))}
                options={overrideTypeOptions}
                disabled={deliveryDisable.isDisableProductOverride}
              />
              <BlockStack align="end">
                <Button
                  onClick={() => handleOverrideAction({ action: ProductOverrideAction.ADD_OVERRIDE_RULE })}
                  icon={PlusIcon}
                  size="large"
                  variant="primary"
                  disabled={deliveryDisable.isDisableProductOverride}
                >
                  Add
                </Button>
              </BlockStack>
            </InlineGrid>
          </BlockStack>
          {deliverySettings.overrideType === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS &&
            deliveryOverrideSpecificWorkingDays.map((item, index) => (
              <Fragment key={index}>
                {index !== 0 && <Divider borderColor="border" />}
                <DateRangeBlock
                  dateRules={item}
                  onRemove={() => handleOverrideAction({ action: ProductOverrideAction.REMOVE_OVERRIDE_RULE, index })}
                  onAddDateRange={() => handleOverrideAction({ action: ProductOverrideAction.ADD_DATE_RANGES, index })}
                  onRemoveDateRange={(dateRangeIndex) =>
                    handleOverrideAction({ action: ProductOverrideAction.REMOVE_DATE_RANGES, index, dateRangeIndex })
                  }
                  onUpdateDateRange={(dateRangeIndex, updatedDateRange) =>
                    handleOverrideAction({
                      action: ProductOverrideAction.UPDATE_DATE_RANGES,
                      index,
                      dateRangeIndex,
                      updatedDateRange,
                    })
                  }
                  onSelectProducts={() =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHOOSE_PRODUCTS,
                      index,
                      filteredItems:
                        deliverySettings.productAvailabilityType === AvailabilityType.ALL
                          ? overrideSpecificWorkingDaysProductsIdChosen.filter((id) => !item.productIds.includes(id))
                          : deliveryProducts.filter(
                              (id) => !overrideSpecificWorkingDaysProductsIdChosen.includes(id) || item.productIds.includes(id),
                            ),
                    })
                  }
                  onSelectCollections={() =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHOOSE_COLLECTIONS,
                      index,
                      filteredItems:
                        deliverySettings.productAvailabilityType === AvailabilityType.ALL
                          ? overrideSpecificWorkingDaysCollectionsIdChosen.filter((id) => !item.collectionIds.includes(id))
                          : deliveryCollections.filter(
                              (id) =>
                                !overrideSpecificWorkingDaysCollectionsIdChosen.includes(id) || item.collectionIds.includes(id),
                            ),
                    })
                  }
                  productsIdChosen={overrideSpecificWorkingDaysProductsIdChosen}
                  collectionsIdChosen={overrideSpecificWorkingDaysCollectionsIdChosen}
                />
              </Fragment>
            ))}
          {deliverySettings.overrideType === ProductOverrideType.OVERRIDE_HOLIDAYS &&
            deliveryOverrideHolidays.map((item, index) => (
              <Fragment key={index}>
                {index !== 0 && <Divider borderColor="border" />}
                <DateRangeBlock
                  dateRules={item}
                  onRemove={() => handleOverrideAction({ action: ProductOverrideAction.REMOVE_OVERRIDE_RULE, index })}
                  onAddDateRange={() => handleOverrideAction({ action: ProductOverrideAction.ADD_DATE_RANGES, index })}
                  onRemoveDateRange={(dateRangeIndex) =>
                    handleOverrideAction({ action: ProductOverrideAction.REMOVE_DATE_RANGES, index, dateRangeIndex })
                  }
                  onUpdateDateRange={(dateRangeIndex, updatedDateRange) =>
                    handleOverrideAction({
                      action: ProductOverrideAction.UPDATE_DATE_RANGES,
                      index,
                      dateRangeIndex,
                      updatedDateRange,
                    })
                  }
                  onSelectProducts={() =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHOOSE_PRODUCTS,
                      index,
                      filteredItems:
                        deliverySettings.productAvailabilityType === AvailabilityType.ALL
                          ? overrideHolidaysProductsIdChosen.filter((id) => !item.productIds.includes(id))
                          : deliveryProducts.filter(
                              (id) => !overrideHolidaysProductsIdChosen.includes(id) || item.productIds.includes(id),
                            ),
                    })
                  }
                  onSelectCollections={() =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHOOSE_COLLECTIONS,
                      index,
                      filteredItems:
                        deliverySettings.productAvailabilityType === AvailabilityType.ALL
                          ? overrideHolidaysCollectionsIdChosen.filter((id) => !item.collectionIds.includes(id))
                          : deliveryCollections.filter(
                              (id) => !overrideHolidaysCollectionsIdChosen.includes(id) || item.collectionIds.includes(id),
                            ),
                    })
                  }
                  productsIdChosen={overrideHolidaysProductsIdChosen}
                  collectionsIdChosen={overrideHolidaysCollectionsIdChosen}
                />
              </Fragment>
            ))}
          {deliverySettings.overrideType === ProductOverrideType.OVERRIDE_DATE_RANGES &&
            deliveryOverrideDateRanges.map((item, index) => (
              <Fragment key={index}>
                {index !== 0 && <Divider borderColor="border" />}
                <OverrideTimeBlock
                  dateRangeRules={item}
                  productsIdChosen={overrideDateRangeProductsIdChosen}
                  collectionsIdChosen={overrideDateRangeCollectionsIdChosen}
                  onRemove={() => handleOverrideAction({ action: ProductOverrideAction.REMOVE_OVERRIDE_RULE, index })}
                  onSelectProducts={() =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHOOSE_PRODUCTS,
                      index,
                      filteredItems:
                        deliverySettings.productAvailabilityType === AvailabilityType.ALL
                          ? overrideDateRangeProductsIdChosen.filter((id) => !item.productIds.includes(id))
                          : deliveryProducts.filter(
                              (id) => !overrideDateRangeProductsIdChosen.includes(id) || item.productIds.includes(id),
                            ),
                    })
                  }
                  onSelectCollections={() =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHOOSE_COLLECTIONS,
                      index,
                      filteredItems:
                        deliverySettings.productAvailabilityType === AvailabilityType.ALL
                          ? overrideDateRangeCollectionsIdChosen.filter((id) => !item.collectionIds.includes(id))
                          : deliveryCollections.filter(
                              (id) => !overrideDateRangeCollectionsIdChosen.includes(id) || item.collectionIds.includes(id),
                            ),
                    })
                  }
                  onChangePreparationDay={(value) =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHANGE_OVERRIDE_PREPARATION_DAY,
                      index,
                      dayValue: Number(value),
                    })
                  }
                  onChangeDateRange={(value) =>
                    handleOverrideAction({
                      action: ProductOverrideAction.CHANGE_OVERRIDE_DATERANGE_DAY,
                      index,
                      dayValue: Number(value),
                    })
                  }
                />
              </Fragment>
            ))}
        </BlockStack>
      </div>
    </LayoutSettings>
  );
};
